import { createGlobalStyle } from 'styled-components';

// Users asked for the report pages at 80% (login / school selection stay at 100%).
// App styles are px-based (theme + hard-coded px), so CSS `zoom` is the practical way to
// scale everything. Change the scale here only.
export const UI_SCALE = 0.8;

// Applied on <html> (not on a wrapper) so portals, modals, popovers and the nav bar
// scale together with the page. Render <ReportScaleStyle /> only inside report layouts:
// styled-components removes it again when the layout unmounts (e.g. back to /schools).
export const ReportScaleStyle = createGlobalStyle`
  html {
    zoom: ${UI_SCALE};
  }
`;

// `zoom` also shrinks viewport units, so `100vh` would only fill 80% of the window.
// Use these for anything inside a scaled layout that must match the real window size.
// e.g. height: ${viewportHeight(100)}  → calc(100vh / 0.8)
export const viewportHeight = (vh = 100) => `calc(${vh}vh / ${UI_SCALE})`;
export const viewportWidth = (vw = 100) => `calc(${vw}vw / ${UI_SCALE})`;

// Rendered-vs-layout scale of an element. With standardized CSS zoom (Chrome 128+,
// Firefox 126+) getBoundingClientRect() is already zoomed while offsetWidth is not, so
// this returns UI_SCALE on zoomed pages; on older engines (or 100% pages) it returns 1.
export const getRenderedScale = el => {
  if (!el || !el.offsetWidth) return 1;
  const scale = el.getBoundingClientRect().width / el.offsetWidth;
  return scale > 0 ? scale : 1;
};

// Popper.js v1 modifier (MUI v4 Tooltip). Popper measures the reference with
// getBoundingClientRect() (zoomed) but the popper size with offsetWidth (not zoomed) and then
// positions inside the zoomed page, so the tooltip drifts toward the top-left. This runs
// first (before shift/offset/flip/arrow), converts the reference to layout px and recomputes
// the popper position from it, so every later Popper modifier works in one coordinate space.
// Verified in Chromium 141 with html { zoom: 0.8 } for top/bottom/left/right/-start and flip.
export const compensateCssZoomModifier = {
  enabled: true,
  order: 50, // before `shift` (100)
  fn: data => {
    const scale = getRenderedScale(data.instance.reference);
    // `flip` re-runs earlier modifiers on the same data: never compensate twice.
    if (Math.abs(scale - 1) < 0.01 || data.zoomCompensated) return data;
    data.zoomCompensated = true;

    const reference = {};
    Object.keys(data.offsets.reference).forEach(key => {
      reference[key] = data.offsets.reference[key] / scale;
    });
    data.offsets.reference = reference;

    const { popper } = data.offsets;
    const { width, height } = popper;
    const base = data.placement.split('-')[0];
    let top = reference.top + reference.height / 2 - height / 2;
    let left = reference.left + reference.width / 2 - width / 2;
    if (base === 'top') top = reference.top - height;
    if (base === 'bottom') top = reference.top + reference.height;
    if (base === 'left') left = reference.left - width;
    if (base === 'right') left = reference.left + reference.width;
    data.offsets.popper = { ...popper, top, left, right: left + width, bottom: top + height };
    return data;
  },
};
